import React, { useState, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import Header from './components/Layout/Header';
import LeftSidebar from './components/Sidebar/LeftSidebar';
import RightSidebar from './components/Sidebar/RightSidebar';
import Canvas from './components/Canvas/Canvas';
import EquipmentModal from './components/Equipment/EquipmentModal';
import DesignModal from './components/Designer/DesignModal';
import RackBuilder from './components/Rack/RackBuilder';
import CablePortEditor from './components/Canvas/CablePortEditor';
import ReportView from './components/Reports/ReportView';
import ProposalView from './components/Proposal/ProposalView';
import { useProjectStore, AUTOSAVE_INTERVAL_MS } from './store/projectStore';
import { Layout, FileText, BarChart3, FileCheck, Radio, ClipboardList, FolderOpen, ChevronRight } from 'lucide-react';
import DesignListSidebar from './components/Sidebar/DesignListSidebar';
import ProjectListModal from './components/Project/ProjectListModal';
import NavigationSidebar from './components/Layout/NavigationSidebar';
import TowerView3D from './components/Site/TowerView3D';
import WarehouseManagerView from './components/Stock/WarehouseManagerView';
import WhatsNewView from './components/WhatsNew/WhatsNewView';
import SiteEquipmentModal from './components/Site/EquipmentModal';
import SiteModal from './components/Site/SiteModal';
import SitesListView from './components/Site/SitesListView';
import JobsListView from './components/Site/JobsListView';
import SiteDetailsDrawer from './components/Site/SiteDetailsModal';
import JobModal from './components/Site/JobModal';
import JobDrawer from './components/Site/JobDrawer';
import { useSiteStore } from './store/siteStore';
import { useSitesStore, generateSiteId } from './store/sitesStore';
import { useWarehouseStore } from './store/warehouseStore';
import { useInventoryStore } from './store/inventoryStore';
import { useAuthStore } from './store/authStore';
import { useCollectionStore } from './store/collectionStore';
import { useUsersStore } from './store/usersStore';
import { useStockUiStore } from './store/stockUiStore';
import { useStockNotificationsStore } from './store/stockNotificationsStore';
import LoginView from './components/Auth/LoginView';
import NoCollectionView from './components/Auth/NoCollectionView';
import { Job, JobHistoryEntry, JobSavePayload, Site } from './types/site';
import { isUnassignedSiteId, UNASSIGNED_JOB_SITE } from './lib/unassignedJobSite';
import { getActedByName } from './store/authStore';
import ProjectModal from './components/ProjectModal';
import TabBar from './components/Layout/TabBar';
import CloseTabModal from './components/Layout/CloseTabModal';

type TabType = 'designer' | 'sites' | 'jobs' | 'reports' | 'proposal' | 'analytics' | 'stock' | 'whatsnew' | 'tower3d';

function App() {
  const { initialize: initAuth, isInitialized, session } = useAuthStore();
  const { initialize: initCollections, isInitialized: collectionsReady, reset: resetCollections } = useCollectionStore();
  const { initialize: initWarehouses } = useWarehouseStore();
  const { fetchItems, fetchStockUnits, fetchRecentLogs } = useInventoryStore();
  const { fetchUsers } = useUsersStore();

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    const onOpenStockRequest = () => {
      setActiveTab('stock');
      useStockUiStore.getState().setActiveStockTab('Requests');
    };
    window.addEventListener('openStockRequest', onOpenStockRequest);
    return () => window.removeEventListener('openStockRequest', onOpenStockRequest);
  }, []);

  useEffect(() => {
    if (!session?.user?.id) {
      resetCollections();
      return;
    }
    void initCollections(session.user.id);
  }, [session?.user?.id, initCollections, resetCollections]);

  const [isDesignModalOpen, setIsDesignModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('designer');
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [isSiteDetailsModalOpen, setIsSiteDetailsModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedJobFromList, setSelectedJobFromList] = useState<{ site: Site; jobId: string } | null>(null);
  const [isJobModalOpenFromList, setIsJobModalOpenFromList] = useState(false);
  const [selectedRecurrenceRule, setSelectedRecurrenceRule] = useState<{ site: Site; ruleId: string } | null>(null);
  const [editingRecurrenceSchedule, setEditingRecurrenceSchedule] = useState(false);
  const [jobDrawerOpen, setJobDrawerOpen] = useState(false);
  const [isJobModalOpenForCreate, setIsJobModalOpenForCreate] = useState(false);
  const [selectedSiteForJob, setSelectedSiteForJob] = useState<string | null>(null);
  const [isDesignListOpen, setIsDesignListOpen] = useState(false);
  const [isProjectListOpen, setIsProjectListOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [closeTabModal, setCloseTabModal] = useState<{ isOpen: boolean; designId: string | null; designName: string }>({
    isOpen: false,
    designId: null,
    designName: ''
  });
  const { refreshSites, currentSite, loadLastSite, setSiteListOpen, isSiteListOpen } = useSiteStore();
  const { loadForCollection, saveSiteJobs, saveUnassignedJobs, createJob, createRecurrenceRule, updateRecurrenceRule, endRecurrenceRule, processDueRecurringJobs, getSiteById, getJobById, sites, unassignedJobs, recurrenceRules } = useSitesStore();
  const {
    currentProject: currentDesign,
    selectedRack,
    isRackBuilderOpen,
    setRackBuilderOpen,
    isCablePortEditorOpen,
    openDesigns,
    closeDesignTab,
    appTheme,
  } = useProjectStore(
    useShallow((s) => ({
      currentProject: s.currentProject,
      selectedRack: s.selectedRack,
      isRackBuilderOpen: s.isRackBuilderOpen,
      setRackBuilderOpen: s.setRackBuilderOpen,
      isCablePortEditorOpen: s.isCablePortEditorOpen,
      openDesigns: s.openDesigns,
      closeDesignTab: s.closeDesignTab,
      appTheme: s.appTheme,
    }))
  );

  useEffect(() => {
    const onOpenJob = (event: Event) => {
      const { siteId, jobId } = (event as CustomEvent<{ siteId?: string; jobId: string }>).detail ?? {};
      if (!jobId) return;
      const site = siteId
        ? (getSiteById(siteId) ?? sites.find(s => s.id === siteId))
        : sites.find(s => s.jobs?.some(j => j.id === jobId));
      const unassignedJob = !site ? unassignedJobs.find(j => j.id === jobId) : null;
      if (!site && !unassignedJob) return;
      setActiveTab('jobs');
      setSelectedJobFromList({
        site: site ?? UNASSIGNED_JOB_SITE,
        jobId,
      });
      setJobDrawerOpen(true);
    };
    window.addEventListener('openJobFromNotification', onOpenJob);
    return () => window.removeEventListener('openJobFromNotification', onOpenJob);
  }, [sites, unassignedJobs, getSiteById]);

  useEffect(() => {
    if (!session || !collectionsReady || !currentDesign?.id) return;
    void initWarehouses().then(() => {
      fetchItems();
      fetchStockUnits();
      fetchRecentLogs();
      fetchUsers();
      void useStockNotificationsStore.getState().fetchNotifications();
    });
  }, [
    session,
    collectionsReady,
    currentDesign?.id,
    initWarehouses,
    fetchItems,
    fetchStockUnits,
    fetchRecentLogs,
    fetchUsers,
  ]);

  useEffect(() => {
    if (!session || !collectionsReady || !currentDesign?.id || activeTab !== 'stock') return;
    const refreshDashboard = () => {
      void initWarehouses();
      fetchItems();
      fetchStockUnits();
      fetchRecentLogs();
    };
    const intervalId = window.setInterval(refreshDashboard, AUTOSAVE_INTERVAL_MS);
    const onFocus = () => refreshDashboard();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [
    session,
    collectionsReady,
    currentDesign?.id,
    activeTab,
    fetchItems,
    fetchStockUnits,
    fetchRecentLogs,
    initWarehouses,
  ]);

  useEffect(() => {
    if (!session || !collectionsReady || !currentDesign?.id) {
      useSitesStore.getState().clear();
      return;
    }
    void loadForCollection(currentDesign.id).then(() => {
      refreshSites();
      loadLastSite();
    });
  }, [session, collectionsReady, currentDesign?.id, loadForCollection, refreshSites]);

  const tabs = [
    { id: 'designer', label: 'Designer', icon: Layout },
    { id: 'sites', label: 'Sites', icon: Radio },
    { id: 'jobs', label: 'Jobs', icon: ClipboardList },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'proposal', label: 'Proposal', icon: FileCheck },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // Initialize sites on mount
  React.useEffect(() => {
    refreshSites();
    
    // Listen for site modal open event
    const handleOpenSiteModal = () => {
      setIsSiteModalOpen(true);
    };
    window.addEventListener('openSiteModal', handleOpenSiteModal);
    const handleOpenDesignModal = () => {
      setIsDesignModalOpen(true);
    };
    window.addEventListener('openDesignModal', handleOpenDesignModal);
    const handleOpenProjectList = () => {
      setIsProjectListOpen(true);
    };
    window.addEventListener('openProjectList', handleOpenProjectList);
    const handleOpenJobsManager = () => {
      setActiveTab('jobs');
    };
    window.addEventListener('openJobsManager', handleOpenJobsManager);
    
    const handleOpenTower3D = (event: Event) => {
      const customEvent = event as CustomEvent;
      setActiveTab('tower3d');
    };
    window.addEventListener('openTower3D', handleOpenTower3D);

    const handleOpenSiteDetails = (event: Event) => {
      const siteId = (event as CustomEvent<{ siteId?: string }>).detail?.siteId;
      if (!siteId) return;
      const site = useSitesStore.getState().getSiteById(siteId);
      if (!site) return;
      setSelectedSite(site);
      setIsSiteDetailsModalOpen(true);
      setActiveTab('sites');
    };
    window.addEventListener('openSiteDetails', handleOpenSiteDetails);
    
    return () => {
      window.removeEventListener('openSiteModal', handleOpenSiteModal);
      window.removeEventListener('openDesignModal', handleOpenDesignModal);
      window.removeEventListener('openProjectList', handleOpenProjectList);
      window.removeEventListener('openJobsManager', handleOpenJobsManager);
      window.removeEventListener('openTower3D', handleOpenTower3D);
      window.removeEventListener('openSiteDetails', handleOpenSiteDetails);
    };
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'designer':
        return (
          <>
            <LeftSidebar onCreateDesign={() => setIsDesignModalOpen(true)} />
            <Canvas />
          </>
        );
      case 'sites':
        return (
          <>
            <SitesListView
              onSiteClick={(site) => {
                setSelectedSite(site);
                setIsSiteDetailsModalOpen(true);
              }}
              onJobClick={(site, jobId) => {
                setSelectedJobFromList({ site, jobId });
                setJobDrawerOpen(true);
              }}
            />
            <SiteDetailsDrawer
              isOpen={isSiteDetailsModalOpen}
              onClose={() => {
                setIsSiteDetailsModalOpen(false);
                // Don't clear selectedSite immediately to allow smooth close animation
                setTimeout(() => setSelectedSite(null), 300);
              }}
              site={selectedSite}
              onSwitchToDesigner={() => setActiveTab('designer')}
              onOpenDesignModal={() => setIsDesignModalOpen(true)}
            />
          </>
        );
      case 'jobs':
        return (
          <JobsListView
            onJobClick={(site, jobId) => {
              setSelectedRecurrenceRule(null);
              setEditingRecurrenceSchedule(false);
              setSelectedJobFromList({ site, jobId });
              setJobDrawerOpen(true);
            }}
            onRecurringRuleClick={(site, ruleId) => {
              setSelectedJobFromList(null);
              setJobDrawerOpen(false);
              setSelectedRecurrenceRule({ site, ruleId });
              setEditingRecurrenceSchedule(true);
              setIsJobModalOpenFromList(true);
            }}
            onCreateJob={() => {
              setSelectedRecurrenceRule(null);
              setIsJobModalOpenForCreate(true);
            }}
          />
        );
      case 'reports':
        return <ReportView />;
      case 'proposal':
        return <ProposalView />;
      case 'analytics':
        return (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <BarChart3 size={64} className="mx-auto text-gray-400 mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">Analytics</h2>
              <p className="text-gray-500">Network performance and insights</p>
              <p className="text-sm text-gray-400 mt-2">Coming soon...</p>
            </div>
          </div>
        );
      case 'stock':
        return null; // rendered persistently below — never unmounted
      case 'whatsnew':
        return <WhatsNewView />;
      case 'tower3d':
        return <TowerView3D onBack={() => setActiveTab('sites')} />;
      default:
        return null;
    }
  };

  const handleTabChange = (tab: TabType) => {
    // Close sidebars when switching tabs
    if (isDesignListOpen) {
      setIsDesignListOpen(false);
    }
    if (isSiteListOpen) {
      setSiteListOpen(false);
    }
    setActiveTab(tab);
  };

  const handleCloseTabConfirm = (designId: string, designName: string) => {
    setCloseTabModal({ isOpen: true, designId, designName });
  };

  const handleCloseTabConfirmed = () => {
    if (closeTabModal.designId) {
      closeDesignTab(closeTabModal.designId);
    }
    setCloseTabModal({ isOpen: false, designId: null, designName: '' });
  };

  const handleCloseTabCancel = () => {
    setCloseTabModal({ isOpen: false, designId: null, designName: '' });
  };

  // Generate ID helper
  const generateId = () => generateSiteId();

  // Handle saving job from the list modal
  const handleSaveJobFromList = (payload: JobSavePayload, siteId: string | null) => {
    if (!currentDesign) return;

    if (payload.recurrence) {
      if (selectedRecurrenceRule) {
        void updateRecurrenceRule(selectedRecurrenceRule.ruleId, payload.job, payload.recurrence).then((ok) => {
          if (ok) {
            setIsJobModalOpenFromList(false);
            setSelectedRecurrenceRule(null);
          }
        });
        return;
      }

      void createRecurrenceRule(siteId!, payload.job, payload.recurrence).then((ok) => {
        if (ok) {
          setIsJobModalOpenForCreate(false);
          setSelectedSiteForJob(null);
        }
      });
      return;
    }

    const jobData = payload.job;

    // If editing an existing once-off job
    if (selectedJobFromList && !editingRecurrenceSchedule) {
      const siteInProject = getSiteById(selectedJobFromList.site.id);
      if (!siteInProject) return;

      const currentJobs = siteInProject.jobs || [];
      const existingJob = currentJobs.find((j: Job) => j.id === selectedJobFromList.jobId);

      if (existingJob?.recurrenceRuleId && recurrenceRules.some((r) => r.id === existingJob.recurrenceRuleId)) {
        return;
      }

      if (existingJob) {
        const author = getActedByName() ?? 'Unknown';
        const newEntries: JobHistoryEntry[] = [];
        const track = (field: string, from: string | undefined, to: string | undefined) => {
          const f = from ?? '';
          const t = to ?? '';
          if (f !== t) {
            newEntries.push({ id: `${Date.now()}-${field}`, field, fromValue: f || undefined, toValue: t, changedBy: author, changedAt: new Date() });
          }
        };
        track('status', existingJob.status, jobData.status);
        track('priority', existingJob.priority, jobData.priority);
        track('assignedTo', existingJob.assignedTo, jobData.assignedTo);
        track('dueDate',
          existingJob.dueDate ? new Date(existingJob.dueDate).toLocaleDateString() : undefined,
          jobData.dueDate ? new Date(jobData.dueDate).toLocaleDateString() : undefined,
        );

        const updatedJobs = currentJobs.map((job: Job) =>
          job.id === existingJob.id
            ? { ...job, ...jobData, updatedAt: new Date(), history: [...(existingJob.history ?? []), ...newEntries], comments: existingJob.comments }
            : job
        );

        void saveSiteJobs(siteInProject.id, updatedJobs);
        setIsJobModalOpenFromList(false);
      }
    } else {
      const newJob: Job = {
        ...jobData,
        id: generateId(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      void createJob(newJob, siteId || null).then((ok) => {
        if (ok) {
          setIsJobModalOpenForCreate(false);
          setSelectedSiteForJob(null);
        }
      });
    }
  };

  // Save job directly (from the drawer — comments, complete, etc.)
  const handleSaveJobDirect = (updatedJob: Job) => {
    if (!currentDesign || !selectedJobFromList) return;
    if (isUnassignedSiteId(selectedJobFromList.site.id)) {
      const updatedJobs = unassignedJobs.map((j: Job) =>
        j.id === updatedJob.id ? updatedJob : j
      );
      void saveUnassignedJobs(updatedJobs);
      return;
    }
    const siteInProject = getSiteById(selectedJobFromList.site.id);
    if (!siteInProject) return;
    const updatedJobs = (siteInProject.jobs ?? []).map((j: Job) =>
      j.id === updatedJob.id ? updatedJob : j
    );
    void saveSiteJobs(siteInProject.id, updatedJobs);
  };

  const isLastTab = openDesigns.length === 1;

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#171a1f' }}>
        <p className="text-sm" style={{ color: '#9ca3af' }}>Loading…</p>
      </div>
    );
  }

  if (!session) {
    return <LoginView />;
  }

  // Logged in but collections are still initialising — show a brief loading state.
  if (!collectionsReady) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#171a1f' }}>
        <p className="text-sm" style={{ color: '#9ca3af' }}>Loading collections…</p>
      </div>
    );
  }

  // Logged in + initialised but no collection exists yet → onboarding.
  if (collectionsReady && !currentDesign) {
    return <NoCollectionView />;
  }

  return (
    <div className={`h-screen flex flex-col bg-gray-100 ${appTheme === 'dark' ? 'theme-dark' : 'theme-default'}`}>
      <Header />
      
      {/* Tab Bar - design tabs */}
      {activeTab === 'designer' && (
        <TabBar
          type="design"
          onCloseConfirm={handleCloseTabConfirm}
          onListClick={() => setIsDesignListOpen(!isDesignListOpen)}
          isListOpen={isDesignListOpen}
        />
      )}
      
      {/* Design List Sidebar */}
      <DesignListSidebar 
        isOpen={isDesignListOpen} 
        onClose={() => setIsDesignListOpen(false)} 
      />
      {/* Project List Modal */}
      <ProjectListModal
        isOpen={isProjectListOpen}
        onClose={() => setIsProjectListOpen(false)}
        onCreateNew={() => {
          setIsProjectListOpen(false);
          setIsProjectModalOpen(true);
        }}
      />
      {/* New Project Modal */}
      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
      />
      
      {/* Close Tab Confirmation Modal */}
      <CloseTabModal
        isOpen={closeTabModal.isOpen}
        designName={closeTabModal.designName}
        isLastTab={isLastTab}
        onConfirm={handleCloseTabConfirmed}
        onCancel={handleCloseTabCancel}
      />
      <div className="flex-1 flex overflow-hidden">
        {/* Navigation Sidebar - positioned absolutely to extend from top */}
        <NavigationSidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        
        {/* Main Content Area - with left margin to account for sidebar */}
        <div className="flex-1 flex overflow-hidden ml-12">
          {renderTabContent()}
          {/* Stock Control — always mounted to preserve grid state across tab switches */}
          <div
            className="flex-1 flex overflow-hidden"
            style={{ display: activeTab === 'stock' ? 'flex' : 'none' }}
          >
            <WarehouseManagerView />
          </div>
          {/* Render Right Sidebar globally so it's available on all tabs */}
          <RightSidebar />
        </div>
      </div>

      <EquipmentModal />
      <SiteEquipmentModal />
      
      <RackBuilder 
        isOpen={isRackBuilderOpen}
        onClose={() => setRackBuilderOpen(false)}
        editingRack={selectedRack}
      />
      
      {/* Cable Port Editor - side-by-side port pickers */}
      <CablePortEditor
        isOpen={isCablePortEditorOpen}
        onClose={() => {
          const store = useProjectStore.getState();
          if (store.pendingNewCableId) store.confirmPendingCable();
          store.setCablePortEditorOpen(false);
        }}
      />
      
      <DesignModal 
        isOpen={isDesignModalOpen}
        onClose={() => setIsDesignModalOpen(false)}
      />
      
      <SiteModal
        isOpen={isSiteModalOpen}
        onClose={() => setIsSiteModalOpen(false)}
      />
      
      {/* Job / Recurring Drawer */}
      {(selectedJobFromList || selectedRecurrenceRule) && jobDrawerOpen && (() => {
        const isUnassignedJob = selectedJobFromList
          ? isUnassignedSiteId(selectedJobFromList.site.id)
          : false;
        const siteId = selectedRecurrenceRule?.site.id
          ?? (isUnassignedJob ? null : selectedJobFromList?.site.id);
        if (!siteId && !isUnassignedJob && !selectedRecurrenceRule) return null;

        const siteInProject = isUnassignedJob
          ? UNASSIGNED_JOB_SITE
          : getSiteById(siteId!);
        if (!siteInProject) return null;

        const ruleFromSelection = selectedRecurrenceRule
          ? recurrenceRules.find((r) => r.id === selectedRecurrenceRule.ruleId) ?? null
          : null;

        const job = selectedJobFromList
          ? (isUnassignedJob
            ? unassignedJobs.find((j: Job) => j.id === selectedJobFromList.jobId) ?? null
            : siteInProject.jobs?.find((j: Job) => j.id === selectedJobFromList.jobId) ?? null)
          : null;

        const recurrenceRule = ruleFromSelection ?? (job?.recurrenceRuleId
          ? recurrenceRules.find((r) => r.id === job.recurrenceRuleId) ?? null
          : null);

        return (
          <JobDrawer
            job={job}
            recurrenceRule={recurrenceRule}
            site={siteInProject}
            onClose={() => {
              setJobDrawerOpen(false);
              setSelectedJobFromList(null);
              setSelectedRecurrenceRule(null);
              setEditingRecurrenceSchedule(false);
            }}
            onEdit={() => {
              setEditingRecurrenceSchedule(false);
              setIsJobModalOpenFromList(true);
            }}
            onEditSchedule={recurrenceRule ? () => {
              setSelectedRecurrenceRule({ site: siteInProject, ruleId: recurrenceRule.id });
              setEditingRecurrenceSchedule(true);
              setIsJobModalOpenFromList(true);
            } : undefined}
            onSave={handleSaveJobDirect}
            onSiteClick={isUnassignedJob ? undefined : (s) => {
              setSelectedSite(s);
              setIsSiteDetailsModalOpen(true);
              setActiveTab('sites');
            }}
          />
        );
      })()}

      {/* Job Modal from List (Editing) */}
      <JobModal
        isOpen={isJobModalOpenFromList && (!!selectedJobFromList || !!selectedRecurrenceRule)}
        onClose={() => {
          setIsJobModalOpenFromList(false);
          setEditingRecurrenceSchedule(false);
          if (!selectedJobFromList) {
            setSelectedRecurrenceRule(null);
          }
        }}
        job={!editingRecurrenceSchedule && selectedJobFromList
          ? (isUnassignedSiteId(selectedJobFromList.site.id)
            ? unassignedJobs.find((j: Job) => j.id === selectedJobFromList.jobId) ?? null
            : getSiteById(selectedJobFromList.site.id)?.jobs?.find((j: Job) => j.id === selectedJobFromList.jobId) ?? null)
          : null}
        recurrenceRule={editingRecurrenceSchedule && selectedRecurrenceRule
          ? recurrenceRules.find((r) => r.id === selectedRecurrenceRule.ruleId) ?? null
          : null}
        onSave={handleSaveJobFromList}
        onEndRecurrence={editingRecurrenceSchedule && selectedRecurrenceRule
          ? async (lastOccurrenceDate) => {
            const ok = await endRecurrenceRule(selectedRecurrenceRule.ruleId, lastOccurrenceDate);
            if (ok) {
              setIsJobModalOpenFromList(false);
              setEditingRecurrenceSchedule(false);
              setSelectedRecurrenceRule(null);
              setJobDrawerOpen(false);
            }
            return ok;
          }
          : undefined}
        sites={sites}
        selectedSiteId={selectedRecurrenceRule?.site.id ?? selectedJobFromList?.site.id}
      />

      {/* Job Modal for Creating New Job */}
      <JobModal
        isOpen={isJobModalOpenForCreate}
        onClose={() => {
          setIsJobModalOpenForCreate(false);
          setSelectedSiteForJob(null);
        }}
        job={null}
        onSave={handleSaveJobFromList}
        sites={sites}
        selectedSiteId={selectedSiteForJob}
      />
    </div>
  );
}

export default App;
